import { type BaseSyntheticEvent, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';

import type { ClientId } from '@nesso/ledger-contracts/ids';

import {
    type Payment,
    type PaymentMethod,
    queryKeys as paymentQueryKeys,
    useRefundPaymentOptions,
} from '@/entities/payment';
import { queryKeys as sessionChargeQueryKeys } from '@/entities/session-charge';
import { ApiError } from '@/shared/api/http-client';
import { formatCurrencyFromCents } from '@/shared/lib/format-utils';
import { formatLocalDate, isToday, parseLocalDate } from '@/shared/lib/time-utils';

import {
    type RefundPaymentFormOutput,
    type RefundPaymentFormValues,
    refundPaymentFormSchema,
} from './refund-payment-form.schema';

type Options = {
    clientId: ClientId;
    payment: Payment;
    onSuccess: () => void;
};

export function useRefundPaymentForm({ clientId, payment, onSuccess }: Options) {
    const queryClient = useQueryClient();
    const refundPayment = useMutation({
        ...useRefundPaymentOptions(),
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey: sessionChargeQueryKeys.all });
        },
        onError: () => {
            void queryClient.invalidateQueries({ queryKey: paymentQueryKeys.all });
        },
    });
    const [refundId] = useState(() => crypto.randomUUID());
    const [lockedRequest, setLockedRequest] = useState<NonNullable<
        typeof refundPayment.variables
    > | null>(null);

    const form = useForm<RefundPaymentFormValues, unknown, RefundPaymentFormOutput>({
        resolver: zodResolver(refundPaymentFormSchema),
    });

    const { isSubmitted } = form.formState;
    const liveMethod = useWatch({ control: form.control, name: 'method' });
    const liveRefundedOn = useWatch({ control: form.control, name: 'refundedOn' });
    const method = lockedRequest ? lockedRequest.method : liveMethod;
    const refundedOn = lockedRequest ? lockedRequest.refundedOn : liveRefundedOn;

    const selectMethod = (next: Exclude<PaymentMethod, 'CARD_ON_FILE'>) => {
        if (lockedRequest) return;
        form.setValue('method', next, { shouldValidate: isSubmitted });
        if (next !== 'CHECK') {
            form.setValue('checkNumber', undefined, { shouldValidate: isSubmitted });
        }
    };

    const selectRefundDate = (date: Date) => {
        if (lockedRequest) return;
        form.setValue('refundedOn', isToday(date) ? undefined : formatLocalDate(date), {
            shouldValidate: isSubmitted,
        });
    };

    const send = (request: NonNullable<typeof refundPayment.variables>) => {
        refundPayment.mutate(request, {
            onSuccess: () => {
                toast.success(`${formatCurrencyFromCents(payment.amountCents)} payment refunded`);
                onSuccess();
            },
            onError: (error) => {
                if (
                    error instanceof ApiError &&
                    error.status === 409 &&
                    error.message === 'PAYMENT_ALREADY_REFUNDED'
                ) {
                    setLockedRequest(null);
                    form.setError('root', { message: 'This payment has already been refunded' });
                    return;
                }
                setLockedRequest(request);
                form.setError('root', { message: "Couldn't record the refund. Try again." });
            },
        });
    };

    const submitNew = form.handleSubmit(({ refundedOn: pickedDay, ...data }) => {
        send({
            id: refundId,
            paymentId: payment.id,
            clientId,
            ...data,
            ...(pickedDay ? { refundedOn: pickedDay } : {}),
        });
    });

    const onSubmit = (event?: BaseSyntheticEvent) => {
        if (!lockedRequest) {
            return submitNew(event);
        }
        event?.preventDefault();
        form.clearErrors('root');
        send(lockedRequest);
        return Promise.resolve();
    };

    return {
        form,
        onSubmit,
        isPending: refundPayment.isPending,
        isLocked: lockedRequest !== null,
        method,
        refundDate: refundedOn ? parseLocalDate(refundedOn) : new Date(),
        selectMethod,
        selectRefundDate,
    };
}
